import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, ClipImage } from "../components/Reveal";
import { CTABlock } from "../components/CTABlock";
import { PROJECTS } from "../data/work";
import { SERVICES } from "../data/services";
import { IMG } from "../data/assets";

const STAGES = ["challenge", "inspection", "execution", "evidence", "outcome"];

export default function OurWork() {
  const [serviceFilter, setServiceFilter] = useState("all");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const industries = useMemo(() => [...new Set(PROJECTS.map((p) => p.industry))], []);
  const filtered = PROJECTS.filter(
    (p) => (serviceFilter === "all" || p.service === serviceFilter) && (industryFilter === "all" || p.industry === industryFilter)
  );

  const chip = (active, onClick, label, testid) => (
    <button key={testid} type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
      className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${active ? "border-dcc bg-dcc text-white" : "border-zinc-300 text-zinc-600 hover:border-dcc hover:text-dcc-strong"}`}>
      {label}
    </button>
  );

  return (
    <>
      <SEO title="Our Work — Evidence-Led Cleaning Projects | DCC" description="Anonymised project stories from operating facilities across India: challenge, inspection, execution, evidence and outcome — the DCC way." path="/our-work" />
      <PageHero
        dark
        eyebrow="Our Work"
        lines={["Stories told in", "evidence, not adjectives."]}
        copy="Client identities are withheld where confidentiality applies. Every story follows the same spine: Challenge → Inspection → Execution → Evidence → Outcome."
      >
        <Reveal delay={0.45}>
          <p className="mt-12 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
            Note — field photography from live DCC projects · client identities withheld where confidentiality applies
          </p>
        </Reveal>
      </PageHero>

      <section className="bg-paper py-20 md:py-28">
        <div className="container-x">
          <div className="mb-6 flex flex-wrap items-center gap-2" role="group" aria-label="Filter by service">
            <span className="eyebrow mr-3">Service</span>
            {chip(serviceFilter === "all", () => setServiceFilter("all"), "All", "filter-service-all")}
            {SERVICES.map((s) => chip(serviceFilter === s.id, () => setServiceFilter(s.id), s.name.replace("Commercial ", ""), `filter-service-${s.id}`))}
          </div>
          <div className="mb-14 flex flex-wrap items-center gap-2" role="group" aria-label="Filter by industry">
            <span className="eyebrow mr-3">Industry</span>
            {chip(industryFilter === "all", () => setIndustryFilter("all"), "All", "filter-industry-all")}
            {industries.map((i) => chip(industryFilter === i, () => setIndustryFilter(i), i, `filter-industry-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`))}
          </div>

          {/* the dossier archive — hairline rows, expandable evidence spine */}
          <div className="border-t border-zinc-200" data-testid="work-list">
            {filtered.length === 0 && (
              <p className="border border-dashed border-zinc-300 p-10 text-center font-mono text-sm text-zinc-500" data-testid="work-empty">
                // No published stories match that combination yet.
              </p>
            )}
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.04}>
                <article className="border-b border-zinc-200" data-testid={`work-${p.id}`}>
                  <div className="grid items-center gap-6 py-8 transition-colors duration-300 md:grid-cols-12 md:px-4">
                    <div className="md:col-span-1">
                      <span className={`font-mono text-xs transition-colors duration-300 ${open === p.id ? "text-dcc-strong" : "text-zinc-500"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    {/* every project card links to its permanent case-study page */}
                    <Link to={`/our-work/${p.id}`} data-testid={`work-image-${p.id}`} aria-label={`Open case study: ${p.title}`}
                      className="block transition-opacity duration-300 hover:opacity-90 md:col-span-3">
                      <ClipImage src={IMG[p.image].src} alt={IMG[p.image].alt} ratio="aspect-[16/10]" className="border border-zinc-100" />
                    </Link>
                    <div className="md:col-span-7">
                      <p className="font-mono text-xs uppercase tracking-[0.2em] text-dcc-strong">{p.industry} · {p.region}</p>
                      <h3 className="mt-3 font-display text-2xl font-semibold uppercase leading-tight tracking-tight text-charcoal md:text-3xl">
                        <Link to={`/our-work/${p.id}`} data-testid={`work-open-${p.id}`} className="transition-colors duration-300 hover:text-dcc-strong">
                          {p.title}
                        </Link>
                      </h3>
                      <p className="mt-3 line-clamp-2 max-w-2xl text-sm text-zinc-600">{p.challenge}</p>
                      <button type="button" onClick={() => setOpen(open === p.id ? null : p.id)}
                        data-testid={`work-toggle-${p.id}`} aria-expanded={open === p.id} aria-controls={`work-detail-${p.id}`}
                        className="link-tech mt-4 min-h-[44px] text-zinc-500 hover:text-dcc-strong">
                        {open === p.id ? "Close evidence summary" : "Evidence summary"}
                        <ChevronRight size={15} className={`transition-transform duration-300 ${open === p.id ? "rotate-90 text-dcc-strong" : ""}`} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  <AnimatePresence initial={false}>
                    {open === p.id && (
                      <motion.div id={`work-detail-${p.id}`} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                        <div className="grid gap-px border-t border-zinc-200 bg-zinc-200 sm:grid-cols-2 lg:grid-cols-5">
                          {STAGES.map((stage, si) => (
                            <div key={stage} className="bg-white p-6">
                              <p className="font-mono text-xs uppercase tracking-[0.25em] text-dcc-strong">{String(si + 1).padStart(2, "0")} · {stage}</p>
                              <p className="mt-3 text-xs leading-relaxed text-zinc-600">{p[stage]}</p>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-zinc-200 bg-white px-6 py-5">
                          <Link to={`/our-work/${p.id}`} data-testid={`work-full-${p.id}`} className="link-tech text-charcoal">
                            Open the full case study <span aria-hidden="true" className="text-dcc-strong">→</span>
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-12 max-w-2xl font-mono text-[11px] leading-relaxed text-zinc-500">
              // Field photography from live DCC projects. Detailed evidence dossiers are shared during the survey stage, subject to client confidentiality.
            </p>
          </Reveal>
        </div>
      </section>
      <CTABlock dark />
    </>
  );
}
